import {
  airbusHelicopterCustomerCaseMeta,
  airbusUpNextCustomerCaseMeta,
  PageMeta,
  hondaMarineCustomerCaseMeta,
  brunswickUseCaseCustomerCaseMeta,
  portOperationsCustomerCaseMeta,
  platformMeta,
} from 'constants/pages';

import maritimeIcon1 from 'assets/industries/water-sport.svg';
import maritimeIcon2 from 'assets/industries/buoy.svg';
import maritimeIcon3 from 'assets/industries/float.svg';
import maritimeIcon4 from 'assets/industries/fishing-boat.svg';
import maritimeIcon5 from 'assets/industries/message-in-bottle.svg';
import maritimeIcon6 from 'assets/industries/sup.svg';
import maritimeMain from 'assets/industries/maritime-main.webp';
import maritimeUseCase2 from 'assets/industries/maritime-use-case-2.webp';
import maritimeTopic1 from 'assets/industries/maritime-topic-1.webp';
import maritimeTopic2 from 'assets/industries/maritime-topic-2.webp';

import navyDefenceIcon1 from 'assets/industries/submarine.svg';
import navyDefenceIcon2 from 'assets/industries/drone.svg';
import navyDefenceIcon3 from 'assets/industries/submarine-2.svg';
import navyDefenceIcon4 from 'assets/industries/sensors.svg';
import navyDefenceIcon5 from 'assets/industries/torpedo.svg';
import navyDefenceIcon6 from 'assets/industries/radar.svg';
import navyDefenceMain from 'assets/industries/navy-main.webp';
import navyDefenceTopic1 from 'assets/industries/navy-topic-1.webp';
import navyDefenceTopic2 from 'assets/industries/navy-topic-2.webp';

import aeronauticsIcon1 from 'assets/industries/radar.svg';
import aeronauticsIcon2 from 'assets/industries/airport.svg';
import aeronauticsIcon3 from 'assets/industries/direction-right.svg';
import aeronauticsIcon4 from 'assets/industries/space-warps.svg';
import aeronauticsIcon5 from 'assets/industries/runway.svg';
import aeronauticsIcon6 from 'assets/industries/rainy-snowy-night.svg';
import aeronauticsTopic1 from 'assets/common/images/aeronautics-topic-1.webp';
import aeronauticsTopic2 from 'assets/common/images/aeronautics-topic-2.webp';
import aeronauticsUseCase3 from 'assets/common/images/aeronautics-use-case-3.webp';

import mobileIcon1 from 'assets/industries/sensors.svg';
import mobileIcon2 from 'assets/industries/danger.svg';
import mobileIcon3 from 'assets/industries/cave.svg';
import mobileIcon4 from 'assets/industries/dust.svg';
import mobileIcon5 from 'assets/industries/lime-stone.svg';
import mobileIcon6 from 'assets/industries/bulldozer.svg';
import mobileMain from 'assets/industries/mobile-main.webp';
import mobileTopic1 from 'assets/industries/mobile-topic-1.webp';
import mobileTopic2 from 'assets/industries/mobile-topic-2.webp';
import mobileTopic3 from 'assets/industries/mobile-topic-3.webp';
import mobileUseCase1 from 'assets/industries/mobile-use-case-1.webp';
import mobileUseCase3 from 'assets/industries/mobile-use-case-3.webp';
import BlueHelicopterImage from 'assets/customer-cases/blue-helicopter-big.webp';
import BlueShipImage from 'assets/customer-cases/blue-ship-big.webp';
import BlueAirplaneImage from 'assets/customer-cases/blue-plane-big.webp.webp';

import { TPaddingOptions } from 'types/common';

export interface IndustrySubTopic {
  title: string;
  text: string;
}

interface IndustryTopic {
  title: string;
  text: string;
  list: string[] | IndustrySubTopic[];
  image: string;
}

interface IndustryContactForm {
  title: string;
  description: string;
  buttonLabel: string;
  additionalButton?: string;
  additionalButtonLink?: string;
  padding: TPaddingOptions;
}

interface TopContent {
  type: 'image' | 'video';
  src: string;
  path: string;
}

interface IndustryAccessLink {
  text: string;
  path: string;
}

interface IndustryAccess {
  title: string;
  text: string;
  link?: IndustryAccessLink;
  additionalText?: {
    title: string;
    text: string;
  };
  icons: string[];
  hiddenBlock?: {
    title: string;
    text: string;
  }[];
}

interface IndustryUseCases {
  title: string;
  text: string;
  cases: {
    title: string;
    text: string;
    image: string;
  }[];
}

interface IndustrySee {
  title: string;
  text: string;
  image: string;
  path: string;
}

export interface IndustryCastomerCasesData {
  title: string;
  description: string;
  imageSrc: string;
  link: string;
}

export interface IndustryData {
  meta: PageMeta;
  additionalDescription?: string;
  topContent: TopContent;
  topics: IndustryTopic[];
  access: IndustryAccess;
  useCases: IndustryUseCases;
  castomerCasesData?: IndustryCastomerCasesData;
  see: IndustrySee;
  contactForm: IndustryContactForm;
}

const paddingsOptions: TPaddingOptions = {
  md: '112px 0',
  sm: '64px 0',
  xxs: '64px 16px',
};

export const industriesMeta: PageMeta = {
  url: '/all-industries',
  title: 'Domain-Specific Simulation for Autonomous Systems | AILiveSim Industries & Use Cases',
  description:
    'Discover how AILiveSim accelerates the development and validation of autonomous systems across maritime, defense, mining, airspace, and automotive industries. With configurable, high-fidelity simulation environments tailored to real-world operational challenges, AILiveSim ensures faster deployment, enhanced safety, and robust AI performance.',
};

export const maritimeData: IndustryData = {
  meta: {
    url: '/Industries/maritime',
    title: 'Autonomous Maritime Systems',
    description:
      'AILiveSim provides a high-fidelity simulation environment for testing autonomous navigation and docking systems. It models complex maritime conditions, including dynamic sea states, varying weather patterns, tidal influences, and visibility changes, enabling rigorous validation and performance assessment for both commercial and leisure vessels.',
  },
  topContent: {
    type: 'video',
    src: 'https://www.ailivesim.com/Videos/Maritime_Industry.mp4',
    path: platformMeta.url,
  },
  topics: [
    {
      title: 'Synthetic Maritime Data for AI Perception and Navigation',
      text: 'We help maritime AI teams develop advanced collision avoidance, automated lookout, and intelligent navigation systems for autonomous vessels — powered by high-quality simulated environments and sensor data.',
      list: [
        'Advanced AI for Safer Maritime Operations',
        'Realistic Scenarios for Effective Training and Testing',
        'Transforming Maritime Safety with Simulation Technology',
      ],
      image: maritimeTopic1,
    },
    {
      title: 'Simulate High-Risk Situations Without Leaving Shore',
      text: 'Train and test your AI systems against complex maritime conditions, no need to wait for the next data collection campaign. With AILiveSim, you can recreate critical scenarios with ease, from low visibility and heavy traffic to changing sea states and diverse marine targets, ensuring reliable perception and safe navigation. Our user-friendly interface and powerful scenario editors put advanced simulation at your fingertips.',
      list: [
        {
          title: 'Critical Scenarios',
          text: 'Recreate scenarios from low visibility to heavy traffic with user-friendly tools.',
        },
        {
          title: 'User-Friendly',
          text: 'Powerful scenario editors make advanced simulation accessible to everyone.',
        },
      ],
      image: maritimeTopic2,
    },
  ],
  access: {
    title: 'Access Edge Cases for Realistic Scenarios',
    link: {
      text: 'Explore Sensor Capabilities',
      path: '/sensors',
    },
    text: 'AILiveSim gives you access to a wide range of assets, from floating debris and buoys to kayaks, inflatables, and persons overboard, to accurately populate your maritime scenarios.',
    additionalText: {
      title: 'Need something specific?',
      text: 'Easily upload your own models or request access to our assets library to match your exact needs.',
    },
    icons: [
      maritimeIcon1,
      maritimeIcon2,
      maritimeIcon3,
      maritimeIcon4,
      maritimeIcon5,
      maritimeIcon6,
    ],
  },
  useCases: {
    title: 'Use Cases for Maritime: Enhancing Safety and Efficiency in Navigation',
    text: 'Explore how AILiveSim empowers maritime teams with advanced simulation capabilities. From optimizing path planning to ensuring safe container unloading, our solutions are designed for real-world challenges. Discover the transformative impact of high-fidelity simulations on maritime operations.',
    cases: [
      {
        title: 'Planning Paths that Maximize COLREGs Compliance',
        text: 'In maritime autonomy, planning navigation paths that comply with the International Regulations for Preventing Collisions at Sea (COLREGs) is essential for safety and legal operation. Effective path planning systems must interpret dynamic traffic situations, apply correct right-of-way rules, and make real-time decisions that align with COLREGs standards. Simulation is key to testing these capabilities across diverse scenarios involving multi-vessel interactions and changing conditions. In parallel, simulation also supports the development of training programs that address the specific knowledge and decision-making skills needed for autonomous navigation and COLREGs compliance. AILiveSim 2.0 enables the simulation of complex maritime traffic and rule-based behaviors, helping developers validate and optimize COLREGs-compliant path planning algorithms with confidence.',
        image: maritimeMain,
      },
      {
        title: 'Validating Safe Operation of Container Unloading',
        text: 'Ensuring the safe and efficient unloading of containers is critical in automated port and logistics operations. Validation involves testing robotic and autonomous systems for collision avoidance, load stability, and adherence to operational protocols under various environmental and operational conditions. Intelligent simulation allows teams to identify potential failure points and refine control logic before deployment. AILiveSim 2.0 supports this by simulating complex unloading scenarios, enabling safe, repeatable validation of container handling systems in virtual environments.',
        image: maritimeUseCase2,
      },
      {
        title: 'Testing Auto Docking Algorithms with Intelligent Testing System',
        text: 'Autodocking algorithms require rigorous validation to ensure safe and precise vessel maneuvering in dynamic maritime environments. An intelligent testing system enables automated scenario generation, edge case discovery, and continuous performance monitoring, making it possible to evaluate docking behavior under varying conditions such as wind, currents, or port congestion. This accelerates development while reducing the risks and costs of physical testing. AILiveSim 2.0 enhances this process by providing a high-fidelity, AI-powered simulation environment tailored for testing and refining autodocking systems at scale.',
        image: maritimeTopic2,
      },
    ],
  },
  castomerCasesData: {
    title: 'See How Simulation Transformed AI Auto-Docking for Leisure Boats.',
    description: `
      Discover how a leading maritime OEM developed, tested, and validated an AI-powered auto-docking system for leisure boats using intelligent simulation — accelerating iteration, reducing risk, and paving the way for the industry's first intelligent co-captain solution in complex maritime conditions.
    `,
    imageSrc: BlueShipImage,
    link: brunswickUseCaseCustomerCaseMeta.url,
  },
  see: {
    title: 'See How a Maritime OEM Transformed Auto-Docking with Simulation',
    text: "Discover how one of the world's leading maritime OEMs validated an AI-powered auto-berthing system using high-fidelity simulation — cutting costs, boosting safety, and accelerating development by replacing costly field trials with virtual testing.",
    image: '',
    path: hondaMarineCustomerCaseMeta.url,
  },
  contactForm: {
    title: 'Ready to Simulate Smarter Maritime Operations?',
    description:
      'Book a demo to see AILiveSim in action. Discover how our high-fidelity maritime simulation enables safer, faster development of autonomous vessel systems — from COLREGs-compliant navigation to intelligent autodocking.',
    buttonLabel: 'Book',
    additionalButton: 'Explore Platform',
    additionalButtonLink: platformMeta.url,
    padding: paddingsOptions,
  },
};

export const navyDefenceData: IndustryData = {
  meta: {
    url: '/Industries/navy-defence',
    title: 'Autonomous Navy & Defense Systems',
    description:
      'AILiveSim enables realistic testing and validation of autonomous systems across maritime, aerial, and land domains. Through real-time sensor simulation, teams evaluate navigation, surveillance, and mission-critical behaviors under operationally relevant conditions for a wide range of autonomous assets.',
  },
  topContent: {
    type: 'video',
    src: 'https://www.ailivesim.com/Videos/Defence.mp4',
    path: platformMeta.url,
  },
  topics: [
    {
      title: 'Unified Simulation Across Maritime, Aerial, and Ground Domains',
      text: 'AILiveSim provides a powerful, scalable simulation platform designed to accelerate AI development across all defense domains — maritime, aerial, and ground. Defense teams can generate and test thousands of complex scenarios on demand, enabling faster validation of perception, navigation, and mission systems. By reducing reliance on costly and time-consuming field campaigns, AILiveSim shortens the path from concept to deployment.',
      list: [
        'Accelerated AI Development',
        'Mission-Relevant Scenarios',
        'Mission-Driven Validation',
      ],
      image: navyDefenceTopic1,
    },
    {
      title: 'Train for Complexity, Without Operational Risk',
      text: 'Prepare your autonomous systems for the battlefield without leaving the lab. AILiveSim enables repeatable simulation of dynamic scenarios — from congested ports and rough seas to contested airspace and unstructured ground terrain. Develop and validate mission-critical perception and navigation capabilities in realistic, high-pressure conditions using customizable and user-friendly tools.',
      list: [
        {
          title: 'Cross-Domain Sensor Simulation',
          text: 'Whether simulating radar for aircraft, LiDAR for ground vehicles or naval vessels, AILiveSim supports synchronized multi-sensor environments with high fidelity and realism.',
        },
        {
          title: 'Custom Simulation Workflows',
          text: 'Build, edit, and deploy simulations tailored to evolving defense requirements and hardware systems.',
        },
      ],
      image: navyDefenceTopic2,
    },
  ],
  access: {
    title: 'Access Complex Assets for Threat-Realistic Testing',
    link: {
      text: 'Explore Sensor Capabilities',
      path: '/sensors',
    },
    text: 'Train, test, and refine AI systems in highly dynamic environments, from congested ports and rough seas to complex ground terrain and contested airspace. AILiveSim supports iterative learning, automated testing, and performance monitoring to prepare autonomous technologies for real-world deployment. Our simulation environment enhances operational safety, improves mission readiness, and supports continuous innovation in defense AI.',
    hiddenBlock: [
      {
        title: 'Modular \n Architecture',
        text: 'Modular architecture is essential in the defense sector, allowing systems to be rapidly adapted, upgraded, or reconfigured to meet evolving operational requirements. AILiveSim 2.0 embraces this approach by offering a flexible, plug-and-play simulation framework where components, such as sensors, vehicle models, environments, and AI modules, can be easily integrated or swapped. This enables defense teams to build custom simulation setups for different platforms, missions, and threat scenarios, accelerating development, testing, and deployment across multi-domain operations.',
      },
      {
        title: 'Platform \n Agnostic',
        text: 'In the defense sector, platform agnostic solutions are vital for ensuring interoperability across diverse systems, whether in air, land, sea, or domains. AILiveSim 2.0 is designed to be platform agnostic, enabling seamless integration with various hardware, simulation engines, and software stacks used by defense organizations. This flexibility allows teams to deploy AILiveSim 2.0 across different vehicle types, control systems, and mission architectures, reducing vendor lock-in and ensuring scalable, future-proof simulation capabilities.',
      },
      {
        title: 'Multi-Domain \n Sensor Simulation',
        text: 'Modern defense operations demand precise coordination across air, land, sea, space, and cyber domains, making multi-domain sensor simulation a critical capability. AILiveSim 2.0 supports this by enabling the simulation of a wide range of sensor types, including radar, LiDAR, EO/IR, and RF, across diverse operational environments. This allows defense teams to test sensor fusion, perception algorithms, and situational awareness systems in complex, joint-domain scenarios, enhancing mission effectiveness, interoperability, and threat detection across the battlespace.',
      },
    ],
    icons: [
      navyDefenceIcon1,
      navyDefenceIcon2,
      navyDefenceIcon3,
      navyDefenceIcon4,
      navyDefenceIcon5,
      navyDefenceIcon6,
    ],
  },
  useCases: {
    title: 'Use Cases for Defense: Enabling Safer, Smarter Autonomous Operations',
    text: 'Explore how AILiveSim supports defense innovation through scalable, multi-domain simulation. From naval surveillance to asymmetric warfare, synthetic data and sensor-rich environments help teams develop, validate, and deploy safer, more adaptive AI-driven systems.',
    cases: [
      {
        title: 'Readiness to adapt to new threats through large scale simulation',
        text: 'In the context of defense, and especially naval defense, large-scale simulation plays a critical role in maintaining operational readiness. As threats evolve rapidly, simulation allows forces to test responses to emerging scenarios in a controlled, risk-free environment. From cyberattacks to unmanned swarm tactics, naval teams can train, adapt, and validate new doctrines or technologies at scale, accelerating decision-making and improving resilience without relying solely on live exercises. This continuous virtual readiness ensures fleets remain agile and mission-ready in the face of unpredictable threats. AILiveSim 2.0 empowers this capability by simulating dynamic, multi-domain maritime threat environments, enabling forces to refine strategies and systems through realistic, repeatable training scenarios.',
        image: navyDefenceTopic2,
      },
      {
        title: 'Maritime Surveillance enhanced through synthetic data',
        text: 'Synthetic data is transforming maritime surveillance by enabling the rapid development and testing of AI-powered detection systems. By generating diverse and controlled datasets, ranging from vessel behaviors to rare threat scenarios, synthetic environments allow models to learn faster and more safely. This enhances object recognition, anomaly detection, and situational awareness, even in challenging conditions like low visibility or crowded sea lanes. The result is a more robust, scalable, and cost-effective surveillance capability that adapts quickly to evolving maritime threats.',
        image: maritimeMain,
      },
      {
        title: 'Asymmetric warfare through simulation of large operations',
        text: 'Simulating large-scale operations provides a critical edge in understanding and countering asymmetric warfare tactics. These simulations enable defense forces to replicate complex environments. They facilitate interactions between conventional forces and non-traditional threats, such as guerrilla tactics and autonomous systems. By modeling these scenarios at scale, militaries can explore vulnerabilities, test adaptive strategies, and train decision-makers in high-pressure, unconventional contexts. This readiness helps level the playing field against adversaries who rely on unpredictability and asymmetry to exploit traditional defense structures. AILiveSim 2.0 enables scalable, AI-driven simulation of asymmetric conflict scenarios, helping defense teams validate strategies, stress-test systems, and accelerate training in a controlled, high-fidelity virtual environment.',
        image: navyDefenceTopic1,
      },
      {
        title: 'Improving reliability of drone and obstacle detection with multispectral sensors',
        text: 'Multispectral sensors significantly enhance the reliability of drone-based obstacle detection by capturing data across multiple wavelengths, beyond visible light. This allows drones to detect objects and terrain features that might be obscured by shadows, fog, camouflage, or low-light conditions. By fusing data from different spectral bands, AI models gain a more complete and accurate understanding of the environment, reducing false positives and improving decision-making in critical missions such as search and rescue, surveillance, or autonomous navigation. AILiveSim 2.0 supports this use case by enabling the simulation of diverse environmental conditions and sensor inputs, helping to train and validate multispectral perception systems in realistic, repeatable scenarios before live testing.',
        image: navyDefenceMain,
      },
    ],
  },
  castomerCasesData: {
    title:
      'See how a leading helicopter OEM advanced safety with AI-powered anti-collision simulation',
    description: `
      From low-altitude flights near power lines to foggy urban skies, discover how AILiveSim helped a global OEM validate AI-driven anti-collision systems in conditions once considered untestable.
    `,
    imageSrc: BlueHelicopterImage,
    link: airbusHelicopterCustomerCaseMeta.url,
  },
  see: {
    title:
      'See how a leading helicopter OEM advanced safety with AI-powered anti-collision simulation',
    text: 'From low-altitude flights near power lines to foggy urban skies, discover how AILiveSim helped a global OEM validate AI-driven anti-collision systems in conditions once considered untestable.',
    image: '',
    path: airbusHelicopterCustomerCaseMeta.url,
  },
  contactForm: {
    title: 'Ready to Accelerate Defense AI with Advanced Simulation?',
    description:
      'Discover how our high-fidelity simulation platform helps defense teams develop mission-ready autonomous systems — from cross-domain perception training to repeatable testing in dynamic combat scenarios.',
    buttonLabel: 'Book',
    additionalButton: 'Explore Platform',
    additionalButtonLink: platformMeta.url,
    padding: paddingsOptions,
  },
};

export const aeronauticsData: IndustryData = {
  meta: {
    url: '/Industries/aeronautics',
    title: `Autonomous 
Flight Systems`,
    description:
      'AILiveSim enables high-fidelity simulation for autonomous flight systems, from runway taxiing to in-flight navigation and collision avoidance. Teams validate mission-critical capabilities in realistic virtual environments, accelerating iteration while reducing risk, cost, and reliance on physical testing.',
  },
  topContent: {
    type: 'video',
    src: 'https://www.ailivesim.com/Videos/Aeronautics_industry.mp4',
    path: platformMeta.url,
  },
  topics: [
    {
      title: 'Intelligent Simulation for AI-Driven Aviation',
      text: 'With intelligent scenario generation and automated testing workflows, AILiveSim allows developers to replicate edge cases, weather variations, traffic patterns, and emergency procedures, all without introducing real-world risk. These simulations support continuous integration and verification, enabling faster iteration and higher confidence in AI-driven flight technologies. This is especially valuable for emerging applications like unmanned aerial vehicles (UAVs), advanced air mobility, and autonomous airport ground operations.',
      list: ['Safer Development Cycles', 'Advanced Scenario Training', 'Smarter AI Systems'],
      image: aeronauticsTopic1,
    },
    {
      title: 'Simulate Critical Flight Operations with Confidence',
      text: "With built-in analytics and performance reporting, AILiveSim gives engineering teams deep visibility into how systems behave, respond, and improve over time. This data-driven insight not only speeds up design refinement but also smooths the road to certification. For innovators in aeronautics, AILiveSim is more than a simulation tool—it's a launchpad for safer, smarter, and more autonomous flight.",
      list: [
        {
          title: 'Complex Ground \n Operations',
          text: 'Model mixed traffic, tight runways, and reduced visibility.',
        },
        {
          title: 'Edge-Case Scripting',
          text: 'Replicate difficult environments from urban terrain to emergency protocols.',
        },
      ],
      image: aeronauticsTopic2,
    },
  ],
  access: {
    title: 'Customize Your Airspace \n with Dynamic Assets',
    link: {
      text: 'Explore Sensor Capabilities',
      path: '/sensors',
    },
    text: 'AILiveSim gives teams the flexibility to simulate low-altitude airspaces, obstacle-rich corridors, and airport environments with high-fidelity models, including moving cranes, drones, lighting shifts, and terrain variation.',
    icons: [
      aeronauticsIcon1,
      aeronauticsIcon2,
      aeronauticsIcon3,
      aeronauticsIcon4,
      aeronauticsIcon5,
      aeronauticsIcon6,
    ],
  },
  useCases: {
    title: 'Use Cases for Aeronautics: Smarter Systems from Gate to Air',
    text: 'Explore how AILiveSim helps aviation teams reduce risk and increase system reliability across autonomous flight programs — from AI-powered taxiing to multispectral drone perception and real-time collision avoidance.',
    cases: [
      {
        title: 'Safe Taxiing, Even in the Most Complex Scenarios',
        text: "Getting from landing to gate isn't just routine; it's one of the most demanding phases of flight. With tight taxiways, unpredictable weather, and mixed ground traffic, safe navigation requires real-time awareness, smart planning, and fast decision-making. AILiveSim 2.0 helps aviation teams simulate and test these critical ground operations in dynamic, high-fidelity airport environments, so autonomous systems can be validated and optimized long before they hit the taxiway.",
        image: aeronauticsTopic1,
      },
      {
        title: 'Improving reliability of Drone and obstacle detection with multispectral sensors ',
        text: 'Multispectral sensors take drone perception to the next level, capturing data beyond visible light to detect obstacles hidden by fog, shadows, camouflage, or darkness. By combining inputs across multiple spectral bands, AI models can better interpret complex environments, reducing false positives and improving mission-critical decisions. AILiveSim 2.0 enables teams to simulate diverse conditions and sensor combinations, making it easy to train and validate multispectral detection systems in rich, repeatable virtual scenarios, long before real-world deployment.',
        image: navyDefenceMain,
      },
      {
        title: 'Obstacle Avoidance for Low-Altitude Operations',
        text: 'Low-flying helicopters performing power-line inspections, medical evacuations, or aerial surveys operate in cluttered airspace where utility cables, cranes, and uneven terrain can turn into split-second hazards. Autonomous UAVs sharing the same corridors face identical challenges. AILiveSim 2.0 lets developers script dynamic obstacle fields (moving cranes, crossing drones, rising terrain) and stream realistic sensor data (lidar, radar, optical flow) into their collision-avoidance algorithms. Thousands of near-miss scenarios can be run overnight, giving engineers the confidence that their systems will navigate safely when every second and every meter counts.',
        image: aeronauticsUseCase3,
      },
    ],
  },
  castomerCasesData: {
    title:
      'See how a global aircraft OEM accelerated autonomous taxiing with intelligent simulation',
    description: `
From weather variability to airport traffic complexity, AILiveSim allowed comprehensive, repeatable testing that cut costs and shortened development cycles.
    `,
    imageSrc: BlueAirplaneImage,
    link: airbusUpNextCustomerCaseMeta.url,
  },
  see: {
    title:
      'See how a global aircraft OEM accelerated autonomous taxiing with intelligent simulation',
    text: 'From weather variability to airport traffic complexity, AILiveSim allowed comprehensive, repeatable testing that cut costs and shortened development cycles.',
    image: BlueAirplaneImage,
    path: airbusUpNextCustomerCaseMeta.url,
  },
  contactForm: {
    title: 'Ready to Build Safer, Smarter Autonomous Flight?',
    description:
      'Book a demo to see AILiveSim in action. Explore how our high-fidelity aeronautics simulation helps validate perception, navigation, and safety systems for the next generation of AI-driven flight — all before leaving the lab.',
    buttonLabel: 'Book',
    additionalButton: 'Explore Platform',
    additionalButtonLink: platformMeta.url,
    padding: paddingsOptions,
  },
};

export const mobileData: IndustryData = {
  meta: {
    url: '/Industries/mobile-machines',
    title: 'Mobile Machinery and Mining Operations',
    description:
      'AILiveSim supports safer and faster innovation through high-fidelity simulation of complex, high-risk environments, such as harbors or mining pits. Teams validate autonomous systems in realistic virtual environments, anticipating edge cases and improving system resilience before deployment without exposing people or equipment to risk.',
  },
  topContent: {
    type: 'video',
    src: 'https://www.ailivesim.com/Videos/Mobile_Machinery.mp4',
    path: platformMeta.url,
  },
  topics: [
    {
      title: 'Simulating Complex Mining and Machinery Environments',
      text: 'For innovation teams developing next-generation mining vehicles and other mobile machinery, AILiveSim serves as a powerful development and validation platform. Engineers can iterate quickly on navigation logic, control algorithms, and sensor configurations in a virtual environment that mirrors the operational complexity of active mines. AI-powered testing and performance analytics accelerate R&D cycles, while revealing system vulnerabilities that would be difficult or costly to detect in the field.',
      list: [
        'High-Risk Testing Without Risk',
        'Realistic Edge Case Generation',
        'Accelerated Development',
      ],
      image: mobileTopic1,
    },
    {
      title: 'Test Navigation and Autonomy Before Deployment',
      text: "Ultimately, AILiveSim enables mining companies to reduce downtime, improve decision-making, and confidently scale autonomous technologies, driving efficiency, safety and innovation across the full lifecycle of mining operations. Run advanced virtual tests of autonomous mining systems in controlled, high-fidelity scenarios. Whether you're developing a haul truck or rock classification model, AILiveSim helps uncover system vulnerabilities, streamline iteration, and reduce reliance on expensive field trials.",
      list: [
        {
          title: 'Extreme Terrain Simulation',
          text: 'Safely test systems in tunnels, unstable slopes, and low-light zones.',
        },
        {
          title: 'Sensor Fusion Support',
          text: 'Train with radar, LiDAR, and computer vision to improve obstacle detection and response.',
        },
      ],
      image: mobileTopic3,
    },
  ],
  access: {
    title: 'Custom Assets and Environmental Variability at Your Fingertips',
    link: {
      text: 'Explore Sensor Capabilities',
      path: '/sensors',
    },
    text: 'AILiveSim lets you populate test environments with assets relevant to mining and construction — rocks, terrain deformations, dynamic hazards, and machinery. Add your own models or select from our growing asset library to match operational needs.',
    icons: [mobileIcon1, mobileIcon2, mobileIcon3, mobileIcon4, mobileIcon5, mobileIcon6],
  },
  useCases: {
    title: 'Use Cases for Mobile Machinery: Smarter Development from Quarry to Tunnel',
    text: 'Explore how AILiveSim enhances AI development in mining and heavy machinery. From validating LiDAR datasets to accelerating rock classification and safe vehicle control, our platform enables faster iteration and operational confidence.',
    cases: [
      {
        title: 'Reinforcement learning with Model predictive control',
        text: 'Reinforcement Learning (RL) teaches systems how to make decisions through experience. Model Predictive Control (MPC) ensures those decisions stay safe and within real-world limits. Together, they create a powerful combo: intelligent systems that learn fast and act responsibly. AILiveSim 2.0 brings this fusion to life, offering a dynamic simulation environment where RL agents can be trained and MPC strategies validated across complex, realistic scenarios. All before a single test hits the real world. Smarter, safer autonomy starts here.',
        image: mobileUseCase1,
      },
      {
        title: 'Lidar datasets for detection classification of obstacles',
        text: "LiDAR is the backbone of modern perception systems, capturing rich 3D data to detect and classify everything from vehicles to terrain with centimeter-level precision. Its advantage as an active sensor—unlike cameras, it doesn't rely on ambient light—makes it especially valuable in low-visibility settings like underground mining. But building robust AI models requires more than just raw scans. It takes massive, well-annotated datasets across a wide range of environments.AILiveSim 2.0 makes this easy. It generates high-quality synthetic LiDAR data and customizable test scenarios, so teams can accelerate development, scale training datasets, and stress-test obstacle detection systems under even the toughest conditions.",
        image: mobileMain,
      },
      {
        title: 'Counting and classification of rocks',
        text: 'Accurate rock counting and classification is critical for mining, construction, and geoscience. With computer vision and AI, systems can now analyze visual or LiDAR data to detect individual rocks, measure their size and shape, and classify them by material or fragmentation level—all automatically. AILiveSim 2.0 boosts this process with synthetic datasets and simulation tools that let you train, test, and fine-tune rock detection algorithms in controlled, repeatable environments. Faster insights. Less manual work. More efficient operations.',
        image: mobileUseCase3,
      },
      {
        title: 'Sensor placement and coverage analysis on future prototypes',
        text: "Getting sensor placement right is critical for autonomous performance. Whether it's LiDAR, radar, or cameras, each sensor's position affects range, blind spots, and overall system awareness. With simulation-driven coverage analysis, engineers can fine-tune configurations early, before a single prototype is built. AILiveSim 2.0 makes it fast and easy to test, iterate, and validate sensor layouts in realistic virtual environments, cutting costs, reducing risk, and ensuring your next prototype is built on insight, not guesswork.",
        image: mobileTopic2,
      },
    ],
  },
  castomerCasesData: {
    title: 'See How a Maritime OEM Transformed Auto-Docking with Simulation',
    description:
      'Discover how one of the world’s leading maritime OEMs validated an AI-powered auto-berthing system using high-fidelity simulation — cutting costs, boosting safety, and accelerating development by replacing costly field trials with virtual testing.',
    imageSrc: BlueShipImage,
    link: portOperationsCustomerCaseMeta.url,
  },
  see: {
    title: 'See How a Maritime OEM Transformed Auto-Docking with Simulation',
    text: "Discover how one of the world's leading maritime OEMs validated an AI-powered auto-berthing system using high-fidelity simulation \u2014 cutting costs, boosting safety, and accelerating development by replacing costly field trials with virtual testing.",
    image: '',
    path: '',
  },
  contactForm: {
    title: 'Ready to Accelerate Safer Autonomy in Mobile Machinery?',
    description:
      'Book a demo to see AILiveSim in action. Discover how our simulation platform helps mining and machinery teams build, test, and refine AI-powered systems for harsh terrain, tight spaces, and dynamic environments — before ever hitting the field.',
    buttonLabel: 'Book',
    additionalButton: 'Explore Platform',
    additionalButtonLink: platformMeta.url,
    padding: paddingsOptions,
  },
};

export const INDUSTRIES = [maritimeData, navyDefenceData, aeronauticsData, mobileData];
